// src/services/geoService.js
import { calculateLocationDistance, getCategoriesFromTypes, getCategoriesForPoi, getPlaceTypeForPoi, getSubtitleForPoi, getGuid } from '@/utils/journeyUtils'
import { trackException } from '@/services/analytics'
import { useAppStore } from '@/stores/app'
import { useDataStore } from '@/stores/data'

// Convert Google Places prediction to match the expected format
export const resultToViewModel = (prediction) => ({
	id: prediction.place_id,
	title: prediction.structured_formatting.main_text,
	subTitle: prediction.structured_formatting.secondary_text,
	type: getPlaceType(prediction.types),
	categories: getCategoriesFromTypes(prediction.types, prediction, 'resultToViewModel'),
	placeTypes: prediction.types,
})

// Helper function to map Google place types to expected types
export const getPlaceType = (types = []) => {
	if (types.includes('train_station')) return 'train'
	if (types.includes('subway_station')) return 'subway'
	if (types.includes('transit_station')) return 'station'
	if (types.includes('establishment')) return 'poi'
	if (types.includes('street_address') || types.includes('route')) return 'address'
	if (types.includes('locality') || types.includes('sublocality')) return 'place'
	if (types.includes('plus_code')) return 'pluscode'
	return 'address'
}

// export const getPoiPlaceMainType = (types = []) => {
// 	if (types.includes('train_station')) return 'train'
// 	if (types.includes('subway_station')) return 'subway'
// 	if (types.includes('transit_station')) return 'station'
// 	if (types.includes('establishment')) return 'poi'
// 	if (types.includes('street_address') || types.includes('route')) return 'address'
// 	if (types.includes('locality') || types.includes('sublocality')) return 'place'
// 	return 'address'
// }

// Format address from components
export const formatAddress = (components = []) => {
	const postal = components.find((c) => c.types.includes('postal_code'))
	const city = components.find((c) => c.types.includes('locality'))
	const country = components.find((c) => c.types.includes('country'))
	return [postal?.long_name, city?.long_name, country?.long_name].filter(Boolean).join(', ')
}

// Generate a session token
export const generateSessionToken = () => {
	return ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, (c) => (c ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))).toString(16))
}

export class GeoService {
	constructor(apiKey = 'AIzaSyAPuw9_dcPYYBGREFrZ5-O6hGd1Hz8iDR4') {
		this.apiKey = apiKey
		this.placesService = null
		this.autocompleteService = null
		this.directionsService = null
		this.sessionToken = null
		this.googleMap = null
		this.googleMapsCallback = 'initGoogleCallback'
		//this.proxyUrl = 'http://localhost:3000/proxy/'
		this.proxyUrl = 'https://rvu-api.kogenta.com/api/proxy/'
		this.poiApiBaseUrl = 'https://api-poi-01.kogenta.com/api/place/nearbysearch/trips'
		this.poiApiCode = '_GZdgAUUo3d3ZZ2pQPX-uFvk41nNvV55Y2UZ5XvRaj4xAzFuy0LtCA=='
		this.mapboxApiKey = 'pk.eyJ1IjoiZGF2aWR3YWhsIiwiYSI6ImNpa254dW1qZzAwOWJ3MGxzdTl5OG5kMXIifQ.48IX1HHv0OYXr0fhZ8G2_Q'
	}

	async init() {
		try {
			// Load Google Maps script if not already loaded
			if (!window.google?.maps?.places) {
				await this.loadGoogleMapsScript()
			}
			this.directionsService = new window.google.maps.DirectionsService()
			this.sessionToken = this.generateSessionToken()
		} catch (error) {
			console.error('Failed to initialize Google Places:', error)
			throw error
		}
	}

	loadGoogleMapsScript() {
		return new Promise((resolve, reject) => {
			// If already loaded, resolve immediately
			if (window.google?.maps?.places) {
				resolve()
				return
			}

			// Create callback function
			window[this.googleMapsCallback] = () => {
				resolve()
				// Clean up callback
				delete window[this.googleMapsCallback]
			}

			// Load the script
			const script = document.createElement('script')
			script.src = `https://maps.googleapis.com/maps/api/js?key=${this.apiKey}&libraries=places&loading=async&callback=${this.googleMapsCallback}`
			script.async = true
			script.crossOrigin = 'anonymous'

			script.onerror = () => {
				reject(new Error('Failed to load Google Maps script'))
				// Clean up callback in case of error
				delete window[this.googleMapsCallback]

				const appStore = useAppStore()
				const dataStore = useDataStore()
				const meta = {
					type: 'script_load_error',
					source: script.src,
				}
				trackException(new Error('Failed to load Google Maps script'), meta, appStore, dataStore)
			}

			document.head.appendChild(script)
		})
	}

	generateSessionToken() {
		return generateSessionToken()
	}

	async search(query, location = null) {
		try {
			// Make sure we have an API key
			if (!this.apiKey) {
				throw new Error('Google Places API key is required')
			}

			const url = this.proxyUrl + 'https://places.googleapis.com/v1/places:autocomplete'
			const headers = {
				'Content-Type': 'application/json',
				'X-Goog-Api-Key': this.apiKey,
				'X-Goog-FieldMask': '*',
			}

			// Add session token if available
			if (this.sessionToken) {
				headers['X-Goog-SessionToken'] = this.sessionToken
			}

			const body = {
				input: query,
				includedRegionCodes: ['no', 'se'],
				includeQueryPredictions: true,
				languageCode: 'no',
				regionCode: 'NO',
				sessionToken: this.sessionToken,
			}

			// Add location bias if coordinates are provided
			if (location && location.latitude && location.longitude) {
				body.locationBias = {
					circle: {
						center: {
							latitude: location.latitude,
							longitude: location.longitude,
						},
						radius: 500.0, // 500 meters
					},
				}
			}

			console.warn('Places API v1 request:', body)

			const response = await fetch(url, {
				method: 'POST',
				headers: headers,
				body: JSON.stringify(body),
			})

			if (!response.ok) {
				throw new Error(`Places API returned status: ${response.status}`)
			}

			const data = await response.json()
			console.warn('Places API v1 results:', data)

			if (!data.suggestions || !Array.isArray(data.suggestions)) {
				return []
			}

			// Map the new API response to the expected view model format
			const mappedPredictions = data.suggestions.map((suggestion) => {
				var place = suggestion.placePrediction
				if (!place) return {}
				return {
					id: place.placeId,
					title: place?.structuredFormat?.mainText?.text || '',
					subTitle: place?.structuredFormat?.secondaryText?.text || '',
					placeId: place.placeId,
					type: getPlaceType(place.types),
					categories: getCategoriesFromTypes(place.types, place?.text?.text, 'search'),
					placeTypes: place.types,
					google_place_id: true,
				}
			})
			console.warn('Mapped predictions:', mappedPredictions)
			return mappedPredictions
		} catch (error) {
			console.error('Places Autocomplete error:', error)
			return []
		}
	}

	async getGooglePlaceId(destination = null) {
		try {
			if (!this.apiKey) {
				throw new Error('Google Places API key is required')
			}

			if (!destination || typeof destination !== 'object') return destination
			if (destination.google_place_id) return destination

			const query = destination.title || destination.name || ''
			if (!query) return destination

			const url = this.proxyUrl + 'https://places.googleapis.com/v1/places:searchText'
			const headers = {
				'Content-Type': 'application/json',
				'X-Goog-Api-Key': this.apiKey,
				// This fields trigger the expensice PRO tier - so we skip it for now
				//'X-Goog-FieldMask': 'places.id,places.displayName,places.primaryType,places.types,places.formattedAddress,places.location,places.addressComponents',
				'X-Goog-FieldMask': 'places.id,places.name,places.attributions,places.movedPlaceId',
			}

			const body = {
				textQuery: query,
				languageCode: 'no',
				regionCode: 'NO',
			}

			if (destination && destination.latitude && destination.longitude) {
				// Use POI address to find closets street name, which can improve matching from Google Places
				if (destination.type != 'address') {
					const poiAddress = await this.getAddressFromPoi(destination, true)
					if (poiAddress && poiAddress.streetAddress) {
						const streetName = poiAddress.streetAddress.split(' ')[0] || ''
						// Add street name to improve accuracy
						if (streetName) body.textQuery = body.textQuery + ' ' + streetName
					}
				} else {
					// For address type, we can also try to add postal code and city from subTitle
					if (destination.subTitle) body.textQuery = body.textQuery + ' ' + destination.subTitle
				}

				body.locationBias = {
					circle: {
						center: {
							latitude: destination.latitude,
							longitude: destination.longitude,
						},
						radius: 200.0,
					},
				}
			}

			const response = await fetch(url, {
				method: 'POST',
				headers,
				body: JSON.stringify(body),
			})

			if (!response.ok) {
				throw new Error(`Places API returned status: ${response.status}`)
			}

			const data = await response.json()
			if (!data.places || !Array.isArray(data.places) || data.places.length === 0) return destination

			const placeId = data.places[0]?.id
			if (!placeId) return destination

			return {
				...destination,
				place_id: placeId,
				google_place_id: true,
			}
		} catch (error) {
			console.error('getGooglePlaceId error:', error)
			return destination
		}
	}

	getPlacesFromPoi(pois) {
		return pois.map((poi) => {
			const placeTypes = getPlaceTypeForPoi(poi)
			const meta = poi.meta?.[0] || {}
			const streetName = meta.street || ''
			const streetNumber = meta.number || ''
			const postalCode = meta.postcode || ''
			const postalTown = meta.postal_city || ''
			const formattedAddress = poi.name || [streetName, streetNumber, postalCode, postalTown].filter(Boolean).join(' ').trim()
			return {
				id: poi.id,
				place_id: 'poi_' + poi.id,
				google_place_id: false,
				title: poi.name,
				subTitle: getSubtitleForPoi(poi),
				distance: Math.round(poi.distance),
				type: getPlaceType(placeTypes),
				categories: getCategoriesForPoi(poi, 'getPlacesFromPoi'),
				placeTypes: placeTypes,
				latitude: poi.location?.latitude || null,
				longitude: poi.location?.longitude || null,
				addressComponents: meta && Object.keys(meta).length ? [meta] : [],
				formattedAddress: formattedAddress,
				streetAddress: (streetName + ' ' + streetNumber).trim(),
				postalAddress: [postalCode, postalTown].filter(Boolean).join(' ').trim(),
			}
		})
	}

	async getPlaceForLocation(location, returnAll = false) {
		console.error('getPlaceForLocation SHOULD NOT BE USED ANYMORE', location)

		const url = this.proxyUrl + 'https://places.googleapis.com/v1/places:searchNearby'
		const data = {
			maxResultCount: 5,
			//includedTypes: ['premise', 'street_address'],
			languageCode: 'no',
			regionCode: 'NO',
			locationRestriction: {
				circle: {
					center: {
						latitude: location.latitude,
						longitude: location.longitude,
					},
					radius: 100.0,
				},
			},
		}
		try {
			const response = await fetch(url, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					'X-Goog-Api-Key': this.apiKey,
					'X-Goog-SessionToken': this.sessionToken,
					'X-Goog-FieldMask': 'places.id,places.displayName,places.primaryType,places.types,places.formattedAddress,places.location,places.addressComponents',
				},
				body: JSON.stringify(data),
			})
			if (!response.ok) {
				throw new Error(`Places API returned status: ${response.status}`)
			}
			const result = await response.json()
			console.warn('getPlaceForLocation - NearbySearch', result)

			if (result?.places?.length) {
				// Process all places to normalized format
				const normalizedPlaces = result.places.map((place) => {
					// Calculate distance
					const distance = calculateLocationDistance(location, place.location)

					// Extract address components
					const streetNumber = place.addressComponents.find((c) => c?.types?.includes('street_number'))?.longText ?? ''
					const streetName = place.addressComponents.find((c) => c?.types?.includes('route'))?.longText ?? ''
					const postalCode = place.addressComponents.find((c) => c?.types?.includes('postal_code'))?.longText ?? ''
					const postalTown = place.addressComponents.find((c) => c?.types?.includes('postal_town'))?.longText ?? ''
					const locality = place.addressComponents.find((c) => c?.types?.includes('locality'))?.longText ?? ''

					// Format postal address
					let postalAddress = postalCode + ' ' + postalTown
					if (locality && !postalTown) postalAddress += ' ' + locality

					// Return normalized place object
					return {
						id: place.id,
						place_id: place.id,
						title: place.displayName.text,
						subTitle: place.formattedAddress,
						type: getPlaceType(place.types),
						categories: getCategoriesFromTypes(place.types, place.displayName?.text, 'getPlaceForLocation'),
						placeTypes: place.types,
						latitude: place.location.latitude,
						longitude: place.location.longitude,
						addressComponents: place.addressComponents,
						streetAddress: streetName + ' ' + streetNumber,
						postalAddress: postalAddress,
						distance: distance,
					}
				})

				// Log and return based on returnAll parameter
				if (returnAll) {
					console.warn('NearbySearch RESULT (All):', normalizedPlaces)
					return normalizedPlaces
				} else {
					const firstPlace = normalizedPlaces[0]
					console.warn('NearbySearch FIRST:', result.places[0])
					console.warn('NearbySearch RESULT:', firstPlace)
					return firstPlace
				}
			} else {
				console.warn('NearbySearch NO RESULTS', result)
			}
		} catch (error) {
			console.error('Nearby Search error', error)
		}

		// Default to coordinates if no places found
		const defaultResponse = {
			latitude: location.latitude,
			longitude: location.longitude,
			title: `${location.latitude.toFixed(5)}, ${location.longitude.toFixed(5)}`,
			subTitle: 'GPS-koordinater',
			type: 'gps',
		}

		return returnAll ? [defaultResponse] : defaultResponse
	}

	async getPlaceForLocationMapbox(location, returnAll = false) {
		console.warn('getPlaceForLocationMapbox', location)

		if (!this.mapboxApiKey) {
			throw new Error('Mapbox API key is required')
		}

		try {
			const longitude = location.longitude
			const latitude = location.latitude
			const url = `https://api.mapbox.com/search/searchbox/v1/reverse?longitude=${longitude}&latitude=${latitude}&access_token=${this.mapboxApiKey}&language=no&limit=10&types=poi,address,place,locality`

			const response = await fetch(url, {
				method: 'GET',
				headers: {
					'Content-Type': 'application/json',
				},
			})

			if (!response.ok) {
				throw new Error(`Mapbox Searchbox API returned status: ${response.status}`)
			}

			const data = await response.json()
			const features = data?.features || []
			if (features.length) {
				const normalizedPlaces = features.map((feature) => {
					const properties = feature.properties || {}
					const coordinates = feature.geometry?.coordinates || []
					const featureType = properties.feature_type || properties.type || ''

					let type = 'address'
					if (featureType === 'poi') type = 'poi'
					if (featureType === 'place' || featureType === 'locality') type = 'place'

					const streetName = properties.street || properties.name || ''
					const streetNumber = properties.address_number || ''
					const title = (streetName + ' ' + streetNumber).trim() || properties.name || properties.full_address || 'Unknown address'
					const subTitle = properties.full_address || properties.place_name || ''

					const distance = calculateLocationDistance(location, {
						latitude: coordinates[1],
						longitude: coordinates[0],
					})

					return {
						id: feature.id || properties.mapbox_id,
						place_id: feature.id || properties.mapbox_id,
						title,
						subTitle: subTitle,
						type,
						categories: [],
						placeTypes: properties.types || properties.place_type || [featureType].filter(Boolean),
						latitude: coordinates[1],
						longitude: coordinates[0],
						addressComponents: [],
						formattedAddress: properties.full_address || properties.place_name || subTitle,
						streetAddress: (streetName + ' ' + streetNumber).trim(),
						postalAddress: properties.postcode || '',
						distance,
					}
				})

				if (returnAll) {
					console.warn('Mapbox Searchbox RESULT (All):', normalizedPlaces)
					return normalizedPlaces
				}

				const firstPlace = normalizedPlaces[0]
				console.warn('Mapbox Searchbox FIRST:', firstPlace)
				return firstPlace
			}
		} catch (error) {
			console.error('getPlaceForLocationMapbox error:', error)
		}

		const defaultResponse = {
			latitude: location.latitude,
			longitude: location.longitude,
			title: `${location.latitude.toFixed(5)}, ${location.longitude.toFixed(5)}`,
			subTitle: 'GPS-koordinater',
			type: 'gps',
		}

		return returnAll ? [defaultResponse] : defaultResponse
	}

	async getPlaceDetails(place) {
		// Only request the fields you were using before
		const fields = ['name', 'formatted_address', 'address_components', 'location', 'types'].join(',')

		// Use either the new place_id or the old id
		const placeId = place.place_id || place.id

		// Return early if no placeId
		if (!placeId) {
			return null
		}

		// Construct the request URL
		const url = `${this.proxyUrl}https://places.googleapis.com/v1/places/${placeId}?fields=${fields}&key=${this.apiKey}&languageCode=no&sessionToken=${this.sessionToken}`

		try {
			// This seems to trigger a CORS error
			const headers = { 'Content-Type': 'application/json' }
			if (this.sessionToken) headers['X-Goog-SessionToken'] = this.sessionToken

			const response = await fetch(url, {
				method: 'GET',
				headers: headers,
			})

			if (!response.ok) {
				const error = new Error(`Places API error: ${response.status} ${response.statusText} - placeId: ${placeId}`)
				error.object = place
				throw error
			}

			const data = await response.json()

			// Add details to the place object
			place.latitude = data.location.latitude
			place.longitude = data.location.longitude
			place.types = getPlaceType(data.types)
			place.categories = getCategoriesFromTypes(data.types, place.title, 'getPlaceDetails')
			place.placeTypes = data.types
			place.formattedAddress = data.formattedAddress
			place.addressComponents = data.addressComponents

			return place
		} catch (error) {
			console.error('Place Details v1 API error:', error)
			throw error
		}
	}

	formatAddress(components = []) {
		return formatAddress(components)
	}

	async getPlaceOrAddressForLocation(location) {
		var places = await this.getPlaceForLocation(location, true)
		var address = await this.getAddressForLocation(location)
		// Check if any of the places match the address
		var place = places.find((p) => p.streetAddress == address.streetAddress && p.postalAddress == address.postalAddress)
		if (place) {
			console.warn('getPlaceOrAddressForLocation found matching place:', place)
			return place
		}
		return address
	}

	async getAddressFromPoi(location, nearest = false) {
		const url = `${this.poiApiBaseUrl}?type=address&code=${encodeURIComponent(this.poiApiCode)}`

		let trip = {
			id: getGuid(),
			origin: { latitude: location.latitude, longitude: location.longitude },
			destination: { latitude: 0, longitude: 0 },
		}

		const body = {
			includeMetadata: true,
			featuresOnly: false,
			featureFilter: ['address'],
			radius: 500,
			trips: [trip],
		}

		try {
			const response = await fetch(url, {
				method: 'POST',
				headers: { Accept: '*/*', 'Content-Type': 'application/json; charset=UTF-8' },
				body: JSON.stringify(body),
			})

			if (!response.ok) {
				throw new Error(`GeoCoder - getAddressFromPoi API returned status: ${response.status}`)
			}

			const tripsWithPois = await response.json()
			const origin = tripsWithPois?.[0]?.origin
			const places = origin?.places || []
			if (places.length > 0) {
				const normalizeCityCase = (value) => {
					if (!value || typeof value !== 'string') return ''
					return value.toLowerCase().replace(/(^|\s|-)(\S)/g, (match, sep, char) => `${sep}${char.toUpperCase()}`)
				}
				const countryCode = origin?.countryCode?.toUpperCase?.() || ''
				const normalizedAddresses = places.map((poi) => {
					const meta = poi?.m?.[0] || {}
					const streetName = meta.street || ''
					const streetNumber = meta.number || ''
					const postalCode = meta.postcode || ''
					const postalTown = normalizeCityCase(meta.postal_city || '')
					const title = (streetName + ' ' + streetNumber).trim() || poi.n || 'Unknown address'
					const subTitle = [postalCode, postalTown].filter(Boolean).join(' ').trim() || poi.n || ''
					const type = poi.f === 'address' ? 'address' : 'place'

					return {
						id: poi.id,
						place_id: 'poi_' + poi.id,
						title,
						subTitle,
						type,
						categories: [],
						placeTypes: [poi.f].filter(Boolean),
						latitude: poi.lat,
						longitude: poi.lon,
						addressComponents: [],
						formattedAddress: poi.n,
						streetAddress: (streetName + ' ' + streetNumber).trim(),
						postalAddress: [postalCode, postalTown].filter(Boolean).join(' ').trim(),
						distance: poi.d,
						countryCode,
					}
				})

				if (!nearest) {
					return normalizedAddresses
				}

				const nearestAddress = normalizedAddresses.reduce((closest, current) => {
					if (!closest) return current
					if (current.distance === undefined) return closest
					if (closest.distance === undefined) return current
					return current.distance < closest.distance ? current : closest
				}, null)

				return nearestAddress
			}
		} catch (error) {
			console.error('GeoCoder - getAddressFromPoi error:', error)
		}

		const fallback = {
			id: '',
			place_id: '',
			title: '',
			latitude: location.latitude,
			longitude: location.longitude,
			subTitle: 'GPS-koordinat',
			type: 'gps',
		}

		return nearest ? fallback : [fallback]
	}

	async getAddressForLocation(location) {
		console.warn('geocodeLocation', location)

		if (!this.apiKey) {
			throw new Error('Google Places API key is required')
		}

		let nearestAddress = null

		try {
			// Construct the geocoding request URL
			const url = `${this.proxyUrl}https://maps.googleapis.com/maps/api/geocode/json?latlng=${location.latitude},${location.longitude}&key=${this.apiKey}&language=no`

			const response = await fetch(url, {
				method: 'GET',
				headers: {
					'Content-Type': 'application/json',
				},
			})

			if (!response.ok) {
				throw new Error(`Geocoding API returned status: ${response.status}`)
			}

			const data = await response.json()
			if (data.status === 'OK' && data.results && data.results.length > 0) {
				// Add distance to all results
				data.results.forEach((result) => {
					result.distance = calculateLocationDistance(location, {
						latitude: result.geometry.location.lat,
						longitude: result.geometry.location.lng,
					})
				})

				const firstResult = data.results[0]
				console.warn('Geocoding ALL:', data.results)

				// Extract address components with defaults to empty strings
				const plusCode = firstResult.address_components.find((c) => c.types.includes('plus_code'))?.long_name ?? ''
				const streetNumber = firstResult.address_components.find((c) => c.types.includes('street_number'))?.long_name ?? ''
				const streetName = firstResult.address_components.find((c) => c.types.includes('route'))?.long_name ?? ''
				const postalCode = firstResult.address_components.find((c) => c.types.includes('postal_code'))?.long_name ?? ''
				const postalTown = firstResult.address_components.find((c) => c.types.includes('postal_town'))?.long_name ?? ''
				const locality = firstResult.address_components.find((c) => c.types.includes('locality'))?.long_name ?? '' // Note: fixed 'longText' to 'long_name'
				const countryCode = firstResult.address_components.find((c) => c.types.includes('country'))?.short_name ?? ''
				const outsideNorway = countryCode != 'NO'

				// Construct title with trimming and fallback
				let title = (streetName + ' ' + streetNumber).trim()
				if (!title) {
					title = firstResult.formatted_address.replace(plusCode, '').trim() || 'Unknown address'
				}

				// Construct subtitle with trimming and fallback
				let subtitle = (postalCode + ' ' + postalTown).trim()
				if (!subtitle && locality) {
					subtitle = locality
				}

				// Get a single place type from the result
				let type = getPlaceType(firstResult.types)

				// Special handling for addresses outside Norway
				if (outsideNorway && type != 'pluscode') {
					var formatedTitleParts = firstResult.formatted_address.split(',')
					if (formatedTitleParts.length > 1) {
						title = formatedTitleParts[0]
						subtitle = formatedTitleParts.slice(1).join(',').trim()
					}
				}

				// Calculate distance
				const distance = calculateLocationDistance(location, {
					latitude: firstResult.geometry.location.lat,
					longitude: firstResult.geometry.location.lng,
				})

				nearestAddress = {
					id: firstResult.place_id,
					place_id: firstResult.place_id,
					title: title,
					subTitle: subtitle,
					type: type,
					categories: getCategoriesFromTypes(firstResult.types, firstResult.formatted_address, 'getAddressForLocation'),
					placeTypes: firstResult.types,
					latitude: firstResult.geometry.location.lat,
					longitude: firstResult.geometry.location.lng,
					addressComponents: firstResult.address_components,
					formattedAddress: firstResult.formatted_address,
					streetAddress: (streetName + ' ' + streetNumber).trim(),
					postalAddress: (postalCode + ' ' + postalTown).trim(), // Updated for consistency
					distance: distance,
					plusCode: plusCode,
				}

				return nearestAddress
			}
		} catch (error) {
			console.error('getAddressForLocation error:', error)
		}

		var gps_title = ''
		var gps_id = ''
		if (nearestAddress?.type == 'pluscode') {
			gps_title = nearestAddress.title
			gps_id = nearestAddress.id
		}

		// Fallback to GPS coordinates if geocoding fails or no results
		return {
			id: gps_id,
			place_id: gps_id,
			title: gps_title,
			latitude: location.latitude,
			longitude: location.longitude,
			subTitle: 'GPS-koordinat',
			type: 'gps',
		}
	}

	async getAddressForLocationMapbox(location) {
		console.warn('geocodeLocationMapbox', location)

		if (!this.mapboxApiKey) {
			throw new Error('Mapbox API key is required')
		}

		let nearestAddress = null
		try {
			const longitude = location.longitude
			const latitude = location.latitude
			const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${longitude},${latitude}.json?access_token=${this.mapboxApiKey}&language=no&types=address&limit=10`

			const response = await fetch(url, {
				method: 'GET',
				headers: {
					'Content-Type': 'application/json',
				},
			})

			if (!response.ok) {
				throw new Error(`Mapbox Geocoding API returned status: ${response.status}`)
			}

			let data = await response.json()
			if (!data?.features?.length) {
				const fallbackUrl = `https://api.mapbox.com/geocoding/v5/mapbox.places/${longitude},${latitude}.json?access_token=${this.mapboxApiKey}&language=no&types=place,locality,postcode&limit=10`
				const fallbackResponse = await fetch(fallbackUrl, {
					method: 'GET',
					headers: {
						'Content-Type': 'application/json',
					},
				})
				if (!fallbackResponse.ok) {
					throw new Error(`Mapbox Geocoding API returned status: ${fallbackResponse.status}`)
				}
				data = await fallbackResponse.json()
			}
			if (data?.features?.length) {
				const firstResult = data.features[0]
				const context = firstResult.context || []
				const postalCode = context.find((c) => c.id?.startsWith('postcode.'))?.text ?? ''
				const locality = context.find((c) => c.id?.startsWith('place.'))?.text ?? ''
				const countryCode = context.find((c) => c.id?.startsWith('country.'))?.short_code?.toUpperCase() ?? ''

				const streetName = firstResult.text ?? ''
				const streetNumber = firstResult.address ?? ''
				let title = (streetName + ' ' + streetNumber).trim()
				if (!title) {
					title = firstResult.place_name || 'Unknown address'
				}

				let subtitle = [postalCode, locality].filter(Boolean).join(' ').trim()
				if (!subtitle) {
					subtitle = firstResult.place_name || ''
				}

				let type = 'address'
				if (firstResult.place_type?.includes('place')) type = 'place'

				const distance = calculateLocationDistance(location, {
					latitude: firstResult.center[1],
					longitude: firstResult.center[0],
				})

				nearestAddress = {
					id: firstResult.id,
					place_id: firstResult.id,
					title,
					subTitle: subtitle,
					type,
					categories: [],
					placeTypes: firstResult.place_type || [],
					latitude: firstResult.center[1],
					longitude: firstResult.center[0],
					addressComponents: [],
					formattedAddress: firstResult.place_name,
					streetAddress: (streetName + ' ' + streetNumber).trim(),
					postalAddress: [postalCode, locality].filter(Boolean).join(' ').trim(),
					distance,
					countryCode,
				}

				return nearestAddress
			}
		} catch (error) {
			console.error('getAddressForLocationMapbox error:', error)
		}

		// Fallback to GPS coordinates if geocoding fails or no results
		return {
			id: '',
			place_id: '',
			title: '',
			latitude: location.latitude,
			longitude: location.longitude,
			subTitle: 'GPS-koordinat',
			type: 'gps',
		}
	}

	async getDirectionsForTrip(trip) {
		console.warn('getDirectionsForTrip', trip)

		if (!trip || !trip.origin || !trip.destination || !trip.activity_name) return null

		// Map activity to travel mode
		let travelMode = google.maps.TravelMode.TRANSIT // Default to transit - Tram, Bus, Subway, Train, Boat, Flights
		if (trip.activity_name == 'automotive') travelMode = google.maps.TravelMode.DRIVING
		if (trip.activity_name == 'cycling') travelMode = google.maps.TravelMode.BICYCLING
		if (trip.activity_name == 'walking') travelMode = google.maps.TravelMode.WALKING
		if (trip.activity_name == 'micro') travelMode = google.maps.TravelMode.TWO_WHEELER

		// Configure request
		const request = {
			origin: new google.maps.LatLng(trip.origin.latitude, trip.origin.longitude),
			destination: new google.maps.LatLng(trip.destination.latitude, trip.destination.longitude),
			travelMode: travelMode,
		}

		// use the directions service to get the route
		return new Promise((resolve, reject) => {
			this.directionsService.route(request, (result, status) => {
				if (status === google.maps.DirectionsStatus.OK) {
					console.warn('getDirectionsForTrip', result)
					resolve(result)
				} else {
					console.warn('getDirectionsForTrip error', status)
					reject(status)
				}
			})
		})
	}

	async getNearbyPlacesFromPoi(location) {
		const featureIgnoreFilter = ['amenity', 'building', 'leisure:playground']
		const url = `${this.poiApiBaseUrl}?type=places&code=${encodeURIComponent(this.poiApiCode)}`

		let trip = {
			id: getGuid(),
			origin: { latitude: location.latitude, longitude: location.longitude },
			destination: { latitude: 0, longitude: 0 },
		}

		const body = {
			includeMetadata: true,
			maxResults: 100,
			featureIgnoreFilter,
			featuresOnly: false,
			radius: 500,
			trips: [trip],
		}

		try {
			const response = await fetch(url, {
				method: 'POST',
				headers: { Accept: '*/*', 'Content-Type': 'application/json; charset=UTF-8' },
				body: JSON.stringify(body),
			})

			if (!response.ok) {
				throw new Error(`GeoCoder - getNearbyPois API returned status: ${response.status}`)
			}

			var tripsWithPois = await response.json()
			if (tripsWithPois.length > 0 && tripsWithPois[0].origin && tripsWithPois[0].origin.places) {
				var pois = tripsWithPois[0].origin.places.map((poi) => {
					return {
						id: poi.id,
						name: poi.n,
						distance: poi.d,
						location: {
							latitude: poi.lat,
							longitude: poi.lon,
						},
						type: poi.f,
					}
				})

				console.warn('GeoCoder - getNearbyPois POIs:', pois)
				if (Array.isArray(pois) && pois.length > 0) {
					let places = this.getPlacesFromPoi(pois)
					console.warn('GeoCoder - getNearbyPois converted places:', places)
					if (places && places.length > 0) {
						return places
					}
				}
				return []
			}
		} catch (error) {
			console.error('GeoCoder - getNearbyPois error:', error)
			return []
		}
	}

	async getPlaceAndAddressFromPoiForLocation(location) {
		const [nearbyPlaces, nearbyAddresses] = await Promise.all([this.getNearbyPlacesFromPoi(location), this.getAddressFromPoi(location, false)])
		// Map closest address to each place
		const mappedPlaces = nearbyPlaces.map((place) => this.mapClosestAddressToPlace(place, nearbyAddresses))
		// Combine places and addresses
		const combinedResults = [...mappedPlaces, ...nearbyAddresses]
		// Sort by distance
		combinedResults.sort((a, b) => (a.distance || 0) - (b.distance || 0))

		return combinedResults
	}

	async getNearestAddressFromPoiForLocation(location) {
		// Get places and addresses from POI database
		const addresses = await this.getAddressFromPoi(location)
		// Return nearest address
		return addresses?.length > 0 ? addresses[0] : null
	}

	async getNearestPlaceOrAddressFromPoiForLocation(location) {
		// Get places and addresses from POI database
		const placesAndAddresses = await this.getPlaceAndAddressFromPoiForLocation(location)

		// Use first place or address as default
		let place = placesAndAddresses?.length > 0 ? placesAndAddresses[0] : null

		// Return nearest place or address based on distance criteria
		const places = placesAndAddresses.filter((p) => p.type !== 'address')
		const addresses = placesAndAddresses.filter((p) => p.type === 'address')
		if (places.length > 0 && addresses.length > 0) {
			const closestPlace = places[0]
			const closestAddress = addresses[0]
			if (closestPlace.distance < 50 && closestPlace.distance - closestAddress.distance < 30) {
				place = closestPlace
			}
		}
		return place
	}

	mapClosestAddressToPlace(place, addresses) {
		const closestAddress = addresses.reduce((closest, current) => {
			if (!closest) return current
			const currentDistance = calculateLocationDistance(place, current)
			const closestDistance = calculateLocationDistance(place, closest)
			return currentDistance < closestDistance ? current : closest
		}, null)

		if (!closestAddress) return place
		const closestDistance = calculateLocationDistance(place, closestAddress)
		if (closestDistance > 50) return place

		return {
			...place,
			formattedAddress: closestAddress.formattedAddress || closestAddress.subTitle || place.formattedAddress,
			addressComponents: closestAddress.addressComponents || place.addressComponents,
			streetAddress: closestAddress.streetAddress || place.streetAddress,
			postalAddress: closestAddress.postalAddress || place.postalAddress,
		}
	}
}

// Create and export a singleton instance
export const geoService = new GeoService()
export default geoService
